import React, { FC, useEffect, useMemo, useState } from 'react';
import useCloseFlyouts from 'helpers/hooks/useCloseFlyouts';
import { useFormat } from 'helpers/hooks/useFormat';
import { useCart } from 'frontastic';
import { CartSlideoutProps } from './type';
import CartItem from '../cart/components/cart-item';
import { EmptyState } from '../empty-state';
import OrderPaymentSection from '../order-payment-section';
import CheckoutButton from '../order-summary/components/checkout-button';
import { CurrencyHelpers } from 'helpers/currencyHelpers';
import useLocale from 'helpers/hooks/useLocale';
import Typography from 'components/commercetools-ui/atoms/typography';
import useClassNames from 'helpers/hooks/useClassNames';
import useCostsData from '../order-payment-section/hooks/useCostsData';
import { ImageProps } from 'frontastic/lib/image';
import Link from 'components/commercetools-ui/atoms/link';
import FreeDelivery from 'components/commercetools-ui/atoms/fee-delivery';
import { Order } from 'shared/types/cart';
import { Money } from 'shared/types/product';
import { useCookies } from 'react-cookie';

export interface CartSlideoutNewProps {
  isFreeDeliveryBannerRequired?: boolean;
  order?: Order; // Added missing prop
}

const CartSlideoutNew: FC<CartSlideoutNewProps> = ({
  isFreeDeliveryBannerRequired = true,
  order, // Default value assigned here
}) => {
  const { formatMessage: formatCartMessage } = useFormat({ name: 'cart' });
  const dataReference = 'cart';
  const [cookies, _] = useCookies(['locale_selection']);
  const [currency, setCurrency] = useState('GBP');

  const { loading, costsToRender, total } = useCostsData({ dataReference, order });
  const { data, isEmpty, taxCategoryRate, estimatedShippingPrice } = useCart();

  const isOrderData = false;
  const hasGiftcard = data?.payments?.find((p) => p.giftcardNumber != undefined);
  const hasTaxed = data?.taxed ? true : false;
  const hasShippingInfo = data?.shippingInfo ? true : false;
  const isStoreCollection = data?.custom?.deliveryOption == 'StoreCollection' ? true : false;

  const isListEmpty = isOrderData
    ? order?.lineItems && order?.lineItems.length > 0
      ? false
      : true
    : data?.lineItems && data?.lineItems.length > 0
    ? false
    : true;

  const showExpectedDelivery = isOrderData
    ? false
    : !hasShippingInfo && !isListEmpty && !isStoreCollection
    ? true
    : false;
  const isAllGiftcards = data?.lineItems?.every((item) => item.isGiftcard === true) ?? false;

  const showExpectedTaxDetails = isOrderData ? false : !hasTaxed && !isAllGiftcards && !isListEmpty ? true : false;

  const showEstimatedTotal = isOrderData
    ? false
    : !hasShippingInfo && !isListEmpty && !isStoreCollection
    ? true
    : false;

  const closeFlyouts = useCloseFlyouts();
  const { ctLocale: locale } = useLocale();

  const estimatedTotalAmount: Money = useMemo(() => {
    const totalAmount: number = total?.value?.centAmount || 0;
    const estShppingCost: number = estimatedShippingPrice?.data?.centAmount || 0;
    const estimatedTotalAmount = totalAmount + estShppingCost;
    const estimatedTotalAsMoney: Money = {
      fractionDigits: 2,
      centAmount: estimatedTotalAmount,
      currencyCode: currency,
    };
    return estimatedTotalAsMoney;
  }, [data?.taxed, total, estimatedShippingPrice?.data]);

  //   const renderAdditionalInfo = ({ item }: any) => {
  //     const { optionsOrder, ...attributes } = item?.variant?.attributes || {};
  //     if (!optionsOrder || optionsOrder.length === 0) {
  //       // return null;
  //     }
  //     console.log('here==', item);

  //     // if (Object.hasOwn(item, 'selectedCustomFitOptions')) {
  //     //   return item?.selectedCustomFitOptions?.map((optionKey: string) => {
  //     //     const value = optionKey.split(':');
  //     //     return (
  //     //       <div key={optionKey} className="mt-4">
  //     //         <span className="text-14 font-light capitalize text-secondary-grey">{value[1]}:</span>{' '}
  //     //         <span className="text-14 font-light capitalize text-secondary-grey">{value[2]}</span>
  //     //       </div>
  //     //     );
  //     //   });
  //     // }

  //     const personalizedFields = Object.hasOwn(item, 'personalizedTextFields')
  //       ? Object.entries(item.personalizedTextFields || {}).map(([key, value]) => {
  //           const [label = '', text = ''] = value?.split(':').map((str: string) => str.trim());
  //           if (text) {
  //             return (
  //               <div key={key} className="mt-4">
  //                 <span className="text-14 font-light capitalize text-secondary-grey">{label}:</span>{' '}
  //                 <span className="text-14 font-light capitalize text-secondary-grey">{text}</span>
  //               </div>
  //             );
  //           }
  //           return null;
  //         })
  //       : [];

  //     // Collect options from the optionsOrder
  //     const optionsFields = optionsOrder?.map((optionsKey: string) => {
  //       const value = attributes[optionsKey];
  //       const optionLabel = formatCartMessage({ id: optionsKey, defaultMessage: optionsKey });

  //       if (!value) return null;

  //       return (
  //         <div key={optionsKey} className="mt-4">
  //           <span className="text-14 font-light capitalize text-secondary-grey">{optionLabel.replace(/_/g, ' ')}:</span>{' '}
  //           <span className="text-14 font-light capitalize text-secondary-grey">
  //             {value.charAt(0).toUpperCase() + value.slice(1)}
  //           </span>
  //         </div>
  //       );
  //     });
  //     // Combine and return both arrays of fields
  //     return [...optionsFields, ...personalizedFields];
  //   };
  const totalAmountClassNames = useClassNames(['mt-24 flex items-center justify-between font-medium']);

  useEffect(() => {
    if (cookies?.locale_selection) setCurrency(cookies?.locale_selection['currency']);
  }, [cookies]);

  return (
    <>
      {isEmpty ? (
        <>
          <div
            className="flex items-center
         justify-center py-20"
          >
            <h3
              className="font-body text-20 font-medium leading-4
             text-gray-600"
            >
              {'Your Shopping Cart is empty'}
            </h3>
          </div>
        </>
      ) : (
        <>
          <div className="max-h-[100vh]">
            <div className="mb-10 max-h-85 overflow-y-auto px-12 md:px-22">
              {data?.lineItems?.map((lineItem) => (
                <div key={lineItem.lineItemId} className="flex h-[85px] items-center gap-x-5 border-b border-[#d1c9c4]">
                  {/* Image Section */}
                  <div className="w-[65px]">
                    <Link link={lineItem._url ?? '#'}>
                      <img src={lineItem.variant?.images?.[0]} alt={'img'} className="w-[100%]" />
                    </Link>
                  </div>

                  {/* Product Details Section */}
                  <div className="flex flex-col items-start">
                    <Link link={lineItem._url ?? '#'}>
                      <p className="font-semimedium font-bod text-14 leading-[14px]" title={lineItem.name}>
                        {lineItem.name}
                      </p>
                      {/* {renderAdditionalInfo(lineItem)} */}
                    </Link>
                    {/* Price & Discount Section */}
                  </div>
                  <div className="">
                    {lineItem.discountedPrice ? (
                      <div className="flex items-start gap-5">
                        <span className="text-20 font-medium leading-loose text-accent-red">
                          {CurrencyHelpers.formatForCurrency(lineItem.discountedPrice, locale)}
                        </span>
                        <span className="text-14 font-normal leading-loose text-gray-500 line-through">
                          {CurrencyHelpers.formatForCurrency(lineItem.price ?? 0, locale)}
                        </span>
                      </div>
                    ) : (
                      <span className="font-semimedium font-body text-14 leading-[14px]">
                        {CurrencyHelpers.formatForCurrency(lineItem.price ?? 0, locale)}
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
            {/* <div className="h-[65vh] grow divide-y divide-neutral-400 overflow-auto px-12 md:px-22">
              {data?.lineItems?.map((lineItem) => (
                <CartItem key={lineItem.lineItemId} item={lineItem} />
              ))}
            </div> */}
            <div className="my-10 flex items-center justify-center px-40 py-5">
              <p className="text-center font-body text-14 font-medium leading-4">
                {'FREE UK STANDARD DELIVERY ON ORDERS OVER £50 (excl. N.I).'}
              </p>
            </div>
            <div className="px-12 md:px-22">
              <div className={totalAmountClassNames}>
                <Typography asSkeleton={loading} className="text-13 font-medium normal-case">
                  {total.label}
                  {/* {'Order Subtotal'} */}
                </Typography>
                {showEstimatedTotal && (
                  <Typography asSkeleton={loading} className="text-23 font-bold normal-case">
                    {CurrencyHelpers.formatForCurrency(estimatedTotalAmount, locale)}
                  </Typography>
                )}
                {!showEstimatedTotal && (
                  <Typography asSkeleton={loading} className="text-23 font-bold normal-case">
                    {CurrencyHelpers.formatForCurrency(total.value, locale)}
                  </Typography>
                )}
              </div>
            </div>
            <Link link={'/cart'}>
              <button className="mx-4 my-15 h-50 w-full rounded-md bg-[#1dce8f] px-16 py-10 font-body text-14 font-bold leading-5 text-[#1e1e1e]">
                {'VIEW SHOPPING CART'}
              </button>
            </Link>
            {isFreeDeliveryBannerRequired && showExpectedDelivery && estimatedShippingPrice?.data?.centAmount == 0 && (
              <FreeDelivery></FreeDelivery>
            )}
            <div className="my-5 flex items-center justify-center py-5">
              <p className="text-center font-body text-14 font-normal leading-[18px] text-[#666]">
                {'Delivery options will be shown during the checkout'}
              </p>
            </div>

            {/* <CheckoutButton text={'VIEW SHOPPING CART'} link="/cart" onClick={closeFlyouts} />
            <OrderPaymentSection
              classNames={{
                applyDiscountButton: 'px-12 py-24 md:px-22',
                infoContainer: 'px-12 md:px-22',
                totalAmount: 'pb-20',
                subCost: 'text-14',
              }}
              button={<CheckoutButton text={'VIEW SHOPPING CART'} link="/cart" onClick={closeFlyouts} />}
            /> */}
          </div>
        </>
      )}
    </>
  );
};

export default CartSlideoutNew;
